import { CloseRounded } from '@mui/icons-material'
import { Box, Button, DialogActions, DialogContent, DialogTitle, Tab, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import Dropzone, { ILayoutProps } from 'react-dropzone-uploader'
import { useTranslation } from 'react-i18next'
import { StyledTabs } from '../../../../../shared/components/StyledTabs'
import TabPanel from '../../../../../shared/components/TabPanel'
import { CancelButton, SaveButton } from '../../../../../shared/components/inputs'
import { StyledDialog } from '../../../../../shared/styles/StyledDialog'
import TypeFileView from '../../../../library/ui/components/TypeFileView'
import { MultimediaEditDialogProps } from '../../../models/lessons'

const Layout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }: ILayoutProps) => (
  <div>
    {previews}
    <div {...dropzoneProps}>{files.length < maxFiles && input}</div>
    {files.length > 0 && submitButton}
  </div>
)

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

export const MultimediaEditDialog = ({
  open,
  lessonMultimedia,
  currentTab,
  onClose,
  onSubmit,
  onTabChange,
  getUploadParams
}: MultimediaEditDialogProps) => {
  const { t } = useTranslation(['global'])

  if (!lessonMultimedia) return null

  return (
    <StyledDialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">
            {t('edit')} multimedia {lessonMultimedia.id}
          </Typography>
          <Button onClick={onClose} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, pt: 2 }}>
        <Box component="form" noValidate autoComplete="off">
          <Grid xs={12}>
            <StyledTabs value={currentTab} onChange={onTabChange} aria-label="disabled tabs example">
              <Tab label={t('document')} {...a11yProps(0)} />
              <Tab label={t('cover-image')} {...a11yProps(1)} />
            </StyledTabs>
          </Grid>

          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <Box
                  sx={{
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    justifyContent: 'center',
                    my: 2,
                    borderRadius: 1,
                    gap: 1,
                    backgroundColor: '#FAFAFA'
                  }}
                >
                  <Dropzone
                    getUploadParams={getUploadParams('fileName')}
                    LayoutComponent={Layout}
                    classNames={{ dropzone: 'dzu-dropzone-custom', inputLabel: 'dzu-inputlabel-custom' }}
                    inputContent={t('dropzone-drop-text')}
                  />

                  {!lessonMultimedia.fileName && (
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {`${t('admin.library.preview')}:`}
                      </Typography>
                      <TypeFileView file={lessonMultimedia} type={'lessons'} />
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Grid container spacing={2}>
              <Grid xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {`${t('cover-image')}:`}
                </Typography>
                <Dropzone
                  getUploadParams={getUploadParams('fileCover')}
                  LayoutComponent={Layout}
                  classNames={{ dropzone: 'dzu-dropzone-custom', inputLabel: 'dzu-inputlabel-custom' }}
                  inputContent={t('dropzone-drop-text')}
                  accept="image/*"
                />
                {lessonMultimedia.cover && lessonMultimedia.fileCover && (
                  <Box
                    sx={{
                      p: 1,
                      display: 'grid',
                      justifyContent: 'center',
                      my: 2,
                      borderRadius: 1,
                      gap: 1,
                      backgroundColor: '#FAFAFA'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {`${t('admin.library.preview')}:`}
                    </Typography>
                    <Box
                      component="img"
                      src={`${import.meta.env.VITE_AWS_URL}/lessons/${lessonMultimedia.cover}`}
                      sx={{ width: '75%', height: 'auto' }}
                      alt={lessonMultimedia.cover}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </DialogContent>

      <DialogActions>
        <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
        <SaveButton onClick={onSubmit} data-lessonmultimedia="saveButton">
          {t('save')}
        </SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}
